import { useEffect, useRef, useState } from "react";
import { Bot, Send, X, Loader2, MessageCircle, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { API, api, errMsg } from "@/lib/api";
import { COMPANY } from "@/lib/data";

const Callback = ({ onDone }) => {
  const [f, setF] = useState({ name: "", phone: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...f, kind: "callback", subject: "Call back request", message: "Customer requested a call back from the website." });
      toast.success("Thanks! We'll call you back shortly.");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };
  return (
    <form onSubmit={submit} className="p-4 space-y-3 flex-1" data-testid="callback-form">
      <p className="text-sm font-semibold">Request a call back</p>
      <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" className="w-full h-10 rounded-xl bg-muted px-4 text-sm outline-none focus:ring-2 ring-primary" data-testid="callback-name" />
      <input required inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="10-digit mobile" className="w-full h-10 rounded-xl bg-muted px-4 text-sm outline-none focus:ring-2 ring-primary" data-testid="callback-phone" />
      <button type="submit" disabled={sending} className="w-full h-10 rounded-full bg-primary text-primary-foreground text-sm font-semibold" data-testid="callback-submit">{sending ? "Sending…" : "Call me back"}</button>
    </form>
  );
};

const sid = () => {
  let s = localStorage.getItem("onkaal-chat-sid");
  if (!s) {
    s = `s-${crypto.randomUUID()}`;
    localStorage.setItem("onkaal-chat-sid", s);
  }
  return s;
};

const HELLO = { role: "assistant", text: "Hi! I'm the Onkaal Assistant. Ask me about services, the ₹199 inspection, warranty or how to book." };

export const ChatWidget = ({ open, setOpen }) => {
  const [msgs, setMsgs] = useState([HELLO]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("chat");
  const end = useRef(null);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    const q = text.trim();
    if (!q || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    const append = (t) => setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: m[m.length - 1].text + t }]);
    try {
      const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid(), message: q }) });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        append(typeof d.detail === "string" ? d.detail : "Sorry, something went wrong. Please call +91 9986019922.");
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const data = p.replace(/^data: /, "");
          if (data === "[DONE]") continue;
          try { append(JSON.parse(data).t); } catch (err) { /* ignore partial */ }
        }
      }
    } catch (err) {
      append("Sorry, I couldn't connect. Please call or WhatsApp +91 9986019922.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="no-print fixed bottom-36 sm:bottom-24 right-4 sm:right-5 z-50 w-[calc(100vw-2rem)] sm:w-96 h-[520px] max-h-[70vh] card-soft flex flex-col overflow-hidden shadow-2xl fade-up" data-testid="chat-widget">
      <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center gap-3">
        <Bot className="h-5 w-5" /><div className="flex-1"><p className="font-semibold text-sm">Onkaal Assistant</p><p className="text-[11px] opacity-80">AI-powered · replies instantly</p></div>
        <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close"><X className="h-5 w-5" /></button>
      </div>
      <div className="grid grid-cols-3 gap-1 p-2 border-b border-border text-xs font-semibold" data-testid="support-tabs">
        <button onClick={() => setMode("chat")} className={`rounded-lg py-2 inline-flex items-center justify-center gap-1 ${mode === "chat" ? "bg-secondary text-primary" : "hover:bg-muted"}`} data-testid="support-tab-chat"><Bot className="h-4 w-4" />Live Chat</button>
        <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" className="rounded-lg py-2 inline-flex items-center justify-center gap-1 hover:bg-muted" data-testid="support-tab-whatsapp"><MessageCircle className="h-4 w-4 text-[#25D366]" />WhatsApp</a>
        <button onClick={() => setMode("callback")} className={`rounded-lg py-2 inline-flex items-center justify-center gap-1 ${mode === "callback" ? "bg-secondary text-primary" : "hover:bg-muted"}`} data-testid="support-tab-callback"><PhoneCall className="h-4 w-4" />Call Back</button>
      </div>
      {mode === "callback" ? <Callback onDone={() => setMode("chat")} /> : (<>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {msgs.map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-primary text-primary-foreground" : "bg-muted"}`} data-testid={`chat-msg-${i}`}>
            {m.text || <Loader2 className="h-4 w-4 animate-spin" />}
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-border flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={600} placeholder="Type your question…" className="flex-1 h-10 rounded-full bg-muted px-4 text-sm outline-none focus:ring-2 ring-primary" data-testid="chat-input" />
        <button type="submit" disabled={busy} className="h-10 w-10 rounded-full bg-primary text-primary-foreground grid place-items-center disabled:opacity-60" aria-label="Send" data-testid="chat-send"><Send className="h-4 w-4" /></button>
      </form>
      </>)}
    </div>
  );
};
